import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { DocumentsCard } from "@/components/DocumentsCard";
import { EnquireSection } from "@/components/EnquireSection";
import { AppointmentsCard } from "@/components/AppointmentsCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ScrollText, ShieldCheck, Landmark, CreditCard, Download, Loader2, ArrowRight, Send } from "lucide-react";

const SERVICES = [
  { key: "will", label: "Will Instruction", icon: ScrollText, route: "/form", desc: "Record your wishes for your estate." },
  { key: "lpa", label: "Lasting Power of Attorney", icon: ShieldCheck, route: "/lpa", desc: "Appoint attorneys — £300 + VAT per type." },
  { key: "legacy_trust", label: "Legacy Protection Trust", icon: Landmark, route: "/legacy-trust", desc: "Protect your property in trust — £2,950 + VAT." },
];

export default function ClientDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState(null);
  const [polling, setPolling] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const [will, lpa, trust, pay] = await Promise.all([
      api.get("/will"), api.get("/service/lpa"), api.get("/service/legacy_trust"), api.get("/payment/items"),
    ]);
    setState({
      will: will.data.progress,
      lpa: lpa.data.progress,
      legacy_trust: trust.data.progress,
      items: pay.data.items,
    });
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Payment received — thank you!");
          await load(); setPolling(false);
          params.delete("session_id"); setParams(params, { replace: true });
          return;
        }
        if (data.status === "expired" || attempts >= 6) {
          setPolling(false); params.delete("session_id"); setParams(params, { replace: true }); return;
        }
      } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("Payment status poll failed (transient), retrying:", e?.message); }
      setTimeout(poll, 2000);
    };
    poll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- handle stripe return once

  const progressFor = (p) => {
    if (!p) return 0;
    const stages = [p.form_started, p.form_completed, p.payment_status === "paid" || p.payment_status === "pay_at_adviser", p.submitted];
    return Math.round((stages.filter(Boolean).length / stages.length) * 100);
  };

  const submitService = async (key) => {
    setBusy(`submit-${key}`);
    try {
      await api.post(key === "will" ? "/will/submit" : `/service/${key}/submit`);
      toast.success("Instruction submitted. Confirmation emails sent.");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const downloadPdf = async (key) => {
    setBusy(`pdf-${key}`);
    try {
      const url = key === "will" ? "/will/pdf" : `/service/${key}/pdf`;
      const res = await api.get(url, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      a.download = `${key}-Instruction.pdf`; document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { toast.error("Could not download PDF"); }
    finally { setBusy(null); }
  };

  if (!state)
    return <div className="min-h-screen flex items-center justify-center bg-brand-bg"><Loader2 className="w-8 h-8 animate-spin text-brand-green" /></div>;

  const outstanding = state.items.filter((i) => i.payment_status === "unpaid");

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col">
      <SiteHeader />
      <main className="flex-1 max-w-5xl w-full mx-auto px-5 md:px-8 py-10">
        <h1 className="font-serif text-3xl md:text-4xl text-brand-green">Welcome, {user?.name?.split(" ")[0]}</h1>
        <p className="text-brand-muted mt-1">Choose a service to begin, track your progress and manage payments.</p>

        {polling && (
          <div className="mt-6 rounded-lg border border-brand-line bg-white px-4 py-3 flex items-center gap-2 text-sm text-brand-muted" data-testid="payment-polling">
            <Loader2 className="w-4 h-4 animate-spin text-brand-green" /> Confirming your payment…
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-5 mt-6">
          {SERVICES.map((s) => {
            const p = state[s.key];
            const pct = progressFor(p);
            const paid = p?.payment_status === "paid";
            const adviser = p?.payment_status === "pay_at_adviser";
            return (
              <div key={s.key} className="rounded-xl border border-brand-line bg-white p-6 flex flex-col transition-all duration-300 hover:shadow-md" data-testid={`service-card-${s.key}`}>
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-10 h-10 rounded-lg bg-brand-admin text-brand-green flex items-center justify-center"><s.icon className="w-5 h-5" /></span>
                  <h3 className="font-serif text-xl text-brand-green leading-tight">{s.label}</h3>
                </div>
                <p className="text-sm text-brand-muted flex-1">{s.desc}</p>

                <div className="mt-4 flex flex-wrap gap-1.5">
                  <Badge variant="outline" className={p?.form_completed ? "border-brand-success text-brand-success" : "border-brand-line text-brand-muted"}>
                    {p?.form_completed ? "Completed" : p?.form_started ? "In progress" : "Not started"}
                  </Badge>
                  {paid && <Badge className="bg-brand-green text-white">Paid</Badge>}
                  {adviser && <Badge variant="outline" className="border-brand-gold text-brand-gold">Adviser</Badge>}
                  {p?.submitted && <Badge className="bg-brand-green text-white">Submitted</Badge>}
                </div>

                <div className="h-1.5 w-full bg-brand-admin rounded-full overflow-hidden mt-3">
                  <div className="h-full bg-brand-green transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => navigate(s.route)} className="bg-brand-green hover:bg-brand-greenHover text-white" data-testid={`start-${s.key}`}>
                    {p?.form_started ? "Continue" : "Start"} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                  {p?.form_completed && !p?.submitted && (
                    <Button size="sm" variant="outline" onClick={() => submitService(s.key)} disabled={busy === `submit-${s.key}`} className="border-brand-line text-brand-green" data-testid={`submit-${s.key}`}>
                      {busy === `submit-${s.key}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />} Submit
                    </Button>
                  )}
                  {p?.submitted && (
                    <Button size="sm" variant="ghost" onClick={() => downloadPdf(s.key)} disabled={busy === `pdf-${s.key}`} className="text-brand-green" data-testid={`pdf-${s.key}`}>
                      {busy === `pdf-${s.key}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} PDF
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Payment summary */}
        <div className="mt-6 bg-white rounded-xl border border-brand-line p-6 md:p-8 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="payment-summary">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-lg bg-brand-admin text-brand-green flex items-center justify-center"><CreditCard className="w-5 h-5" /></span>
            <div>
              <h3 className="font-serif text-xl text-brand-green">Payments</h3>
              <p className="text-sm text-brand-muted">
                {state.items.length === 0 ? "No payable services yet." :
                  outstanding.length === 0 ? "All selected services are settled." :
                    `${outstanding.length} service(s) awaiting payment. You can pay online or arrange with your adviser.`}
              </p>
            </div>
          </div>
          <Button onClick={() => navigate("/pay")} disabled={state.items.length === 0} className="bg-brand-green hover:bg-brand-greenHover text-white" data-testid="go-to-pay">
            Manage payment <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>

        <DocumentsCard />
        <AppointmentsCard />
        <EnquireSection />
      </main>
      <SiteFooter />
    </div>
  );
}
